import { Badge, Box, Button, Card, Group, Modal, Select, SimpleGrid, Stack, Text, TextInput, ThemeIcon } from '@mantine/core';
import { IconArrowUpRight, IconBuilding, IconForms, IconPlus } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import type { FormSummary } from '../../api/types';
import { PageActions } from '../../components/page-actions';
import { useToast } from '../../components/Toast';
import { tr } from '../../lib/i18n';
import { fmtDate, fmtNum } from '../../lib/labels';
import { ErrorState, Loading, StatusBadge } from '../../ui/kit';

function FormCard({ f, onOpen }: { f: FormSummary; onOpen: () => void }) {
  return (
    <Card padding={0} className="pf-tile" style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column' }} onClick={onOpen}>
      <Box p="lg" style={{ flex: 1 }}>
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
            <ThemeIcon size={40} radius="md" variant="light" color={f.is_default ? 'ink' : 'gray'}><IconForms size={20} stroke={1.6} /></ThemeIcon>
            <Box style={{ minWidth: 0 }}>
              <Text fw={600} truncate>{f.name}</Text>
              <Group gap={6} mt={2}>
                {f.is_default && <Badge size="xs" color="ink" variant="filled" radius="sm">{tr('الافتراضي', 'Default')}</Badge>}
                {f.scope === 'ORG' && <Badge size="xs" variant="default" radius="sm" leftSection={<IconBuilding size={10} />}>{f.organization_name ?? tr('شركة', 'Company')}</Badge>}
              </Group>
            </Box>
          </Group>
          <IconArrowUpRight size={18} stroke={1.6} color="var(--pf-muted)" style={{ flexShrink: 0 }} />
        </Group>
        <Stack gap={8} mt="lg">
          <Group justify="space-between" gap="xs">
            <Text size="sm" c="dimmed">{tr('المنشور', 'Published')}</Text>
            {f.published_version ? (
              <Text size="sm"><Text span fw={600} className="pf-num">v{f.published_version}</Text> <Text span c="dimmed" size="xs">· {fmtDate(f.published_at)}</Text></Text>
            ) : <StatusBadge tone="gray" size="sm">{tr('غير منشور', 'Unpublished')}</StatusBadge>}
          </Group>
          <Group justify="space-between" gap="xs">
            <Text size="sm" c="dimmed">{tr('المسودة', 'Draft')}</Text>
            {f.draft_version ? <StatusBadge tone="orange" size="sm">{tr(`تعديلات غير منشورة · v${f.draft_version}`, `Unpublished changes · v${f.draft_version}`)}</StatusBadge> : <Text size="sm" c="dimmed">{tr('لا يوجد', 'None')}</Text>}
          </Group>
        </Stack>
      </Box>
      <Group justify="space-between" px="lg" py="sm" style={{ borderTop: '1px solid var(--pf-line)' }}>
        <Text size="sm" c="dimmed">
          {f.is_default ? tr('كل الشركات بدون نموذج خاص', 'All companies without a custom form') : <>{tr('مستخدم في', 'Used by')} <Text span fw={600} c="var(--pf-text)" className="pf-num">{fmtNum(f.assigned_orgs)}</Text> {tr('شركة', 'companies')}</>}
        </Text>
        <Text size="sm" fw={600}>{tr('فتح المنشئ', 'Open builder')}</Text>
      </Group>
    </Card>
  );
}

export function FormsListPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { toast, toastError } = useToast();
  const forms = useQuery({ queryKey: ['forms'], queryFn: () => api<{ items: FormSummary[] }>('/forms') });
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [copyFrom, setCopyFrom] = useState('');

  const create = useMutation({
    mutationFn: () => api<{ id: string }>('/forms', { method: 'POST', body: { name, ...(copyFrom ? { copyFromFormId: copyFrom } : {}) } }),
    onSuccess: (f) => {
      toast(tr('تم إنشاء النموذج', 'Form created'));
      qc.invalidateQueries({ queryKey: ['forms'] });
      nav(`/forms/${f.id}`);
    },
    onError: toastError,
  });
  const items = [...(forms.data?.items ?? [])].sort((a, b) => Number(b.is_default) - Number(a.is_default));

  return (
    <>
      <PageActions>
        <Button leftSection={<IconPlus size={16} />} onClick={() => setOpen(true)}>{tr('نموذج إضافي', 'New form')}</Button>
      </PageActions>
      {forms.isLoading ? <Loading /> : forms.isError ? <ErrorState error={forms.error} onRetry={() => forms.refetch()} /> : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
          {items.map((f) => <FormCard key={f.id} f={f} onOpen={() => nav(`/forms/${f.id}`)} />)}
        </SimpleGrid>
      )}
      <Modal opened={open} onClose={() => setOpen(false)} size={440} title={tr('نموذج جديد', 'New form')}>
        <Stack gap="md">
          <TextInput label={tr('اسم النموذج', 'Form name')} withAsterisk value={name} onChange={(e) => setName(e.currentTarget.value)} data-autofocus />
          <Select label={tr('نسخ الحقول من', 'Copy fields from')} value={copyFrom} allowDeselect={false} onChange={(v) => setCopyFrom(v ?? '')}
            data={[{ value: '', label: tr('نموذج فارغ', 'Blank form') }, ...(forms.data?.items ?? []).filter((f) => f.published_version_id).map((f) => ({ value: f.id, label: f.name }))]} />
          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={() => setOpen(false)}>{tr('إلغاء', 'Cancel')}</Button>
            <Button disabled={name.trim().length < 2} loading={create.isPending} onClick={() => create.mutate()}>{tr('إنشاء', 'Create')}</Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
